// src/components/Header.js
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CartDropdown from './CartDropdown';
import { cartCount } from '../products';
import BrandMark from './BrandMark';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Fade,
  IconButton,
  Skeleton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useTheme } from '@mui/material/styles';
import { Mail, MapPin, Phone } from 'lucide-react';
import { useContext } from 'react';
import { ColorModeContext } from '../ColorModeContext';
import { isAdmin as checkAdmin } from '../db';
import { SITE, emailHref, phoneHref } from '../siteInfo';

/**
 * The admin answer is cached for the tab's lifetime only. sessionStorage is
 * deliberate: it dies with the tab, so a shared machine never inherits the
 * previous person's navbar. It decides what is drawn, never what is allowed —
 * the route guard and firestore.rules are what actually protect /admin.
 */
const readAdminCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? null : raw === 'true';
  } catch (err) {
    return null;
  }
};

const writeAdminCache = (key, value) => {
  try {
    sessionStorage.setItem(key, String(value));
  } catch (err) {
    // Private browsing and full quotas both land here. Losing the cache only
    // costs the round trip it was there to avoid.
  }
};

/**
 * The section links. Products and Contact are anchors rather than routes of
 * their own — Contact is the footer — so they carry the hash and Home scrolls
 * to it on arrival, which also makes them work from the cart or login page.
 */
const NAV_LINKS = [
  { label: 'Home', to: '/home', hash: '' },
  { label: 'Products', to: '/home#products', hash: '#products' },
  { label: 'Contact', to: '/home#contact', hash: '#contact' },
];

const isHomeRoute = (pathname) => pathname === '/' || pathname === '/home';

const Header = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [cartQuantity, setCartQuantity] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const dark = theme.palette.mode === 'dark';

  // Olive already lightens with the mode, so the accent is simply the brand.
  const accent = theme.palette.primary.main;

  // The bar condenses once the page has moved: the contact strip rolls up, the
  // toolbar gives back some height and the surface gains weight. Scrolled
  // reading gets more room without the chrome ever leaving.
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  const [isAdminUser, setIsAdminUser] = useState(false);
  // The buttons stay hidden until both who the user is *and* whether they are
  // an admin are known. Revealing them as each answer arrives made the bar
  // rebuild itself twice in front of the reader.
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) return;
      if (!currentUser) {
        setUser(null);
        setIsAdminUser(false);
        setAuthReady(true);
        return;
      }

      // The admin check is a Firestore read and is the slow half. Its answer is
      // remembered per session so a reload paints the final bar immediately
      // rather than waiting for the round trip again; the read still runs, and
      // corrects the cache if the answer has changed.
      const cacheKey = `ndz:isAdmin:${currentUser.uid}`;
      const cached = readAdminCache(cacheKey);
      if (cached !== null) {
        setUser(currentUser);
        setIsAdminUser(cached);
        setAuthReady(true);
      }

      let admin = cached === null ? false : cached;
      try {
        admin = await checkAdmin(currentUser.email);
      } catch (err) {
        // checkAdmin already logs; an unreadable role is simply not an admin.
      }
      if (!active) return;
      writeAdminCache(cacheKey, admin);
      setUser(currentUser);
      setIsAdminUser(admin);
      setAuthReady(true);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  // Listen for cart updates and add up every product in it.
  useEffect(() => {
    function syncCartQuantity() {
      setCartQuantity(cartCount());
    }
    syncCartQuantity();
    window.addEventListener('cartUpdated', syncCartQuantity);
    return () => {
      window.removeEventListener('cartUpdated', syncCartQuantity);
    };
  }, []);

  // A route change is the drawer's cue to close; left open over the new page it
  // is the classic mobile-nav bug.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.hash]);

  const toggleCart = () => setIsCartOpen((prev) => !prev);

  const handleSignOut = async () => {
    setIsMenuOpen(false);
    try {
      await signOut(auth);
      toast.info('You have signed out.');
      navigate('/login');
    } catch (error) {
      toast.error('Error signing out: ' + error.message);
    }
  };

  const isLinkActive = (link) =>
    isHomeRoute(location.pathname) && (location.hash || '') === link.hash;

  /**
   * Home and the wordmark both mean "the top of the site". Routing alone will
   * not do it: clicking Home from /home#products only drops the hash, and the
   * browser leaves the page exactly where it was.
   */
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  /** A nav link, with the rule that grows from the centre on hover. */
  const navSx = (active) => ({
    px: 1.25,
    py: 0.75,
    minWidth: 0,
    fontSize: '0.92rem',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.01em',
    color: active ? accent : 'text.primary',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'transparent', color: accent },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 10,
      right: 10,
      bottom: 4,
      height: 2,
      borderRadius: 2,
      backgroundColor: accent,
      transform: active ? 'scaleX(1)' : 'scaleX(0)',
      transformOrigin: 'center',
      transition: 'transform .25s ease',
    },
    '&:hover::after': { transform: 'scaleX(1)' },
  });

  const authControls = (
    <>
      {/* Only shown once there is somebody to have orders. /orders is behind
          ProtectedRoute, so offering it to a signed-out visitor would only
          bounce them to the login page. */}
      {user && (
        <Button
          component={Link}
          to="/orders"
          size="small"
          startIcon={<ReceiptLongIcon sx={{ fontSize: 17 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: location.pathname === '/orders' ? accent : 'text.primary',
            '&:hover': { color: accent, backgroundColor: 'transparent' },
          }}
        >
          My Orders
        </Button>
      )}
      {isAdminUser && (
        <Button
          component={Link}
          to="/admin"
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            px: 1.75,
            color: accent,
            border: '1px solid',
            borderColor: dark ? 'rgba(179,199,127,0.40)' : 'rgba(85,107,47,0.35)',
            '&:hover': {
              borderColor: accent,
              backgroundColor: dark ? 'rgba(179,199,127,0.12)' : 'rgba(85,107,47,0.08)',
            },
          }}
        >
          Admin
        </Button>
      )}
      {user ? (
        <Button
          onClick={handleSignOut}
          size="small"
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          Sign Out
        </Button>
      ) : (
        <>
          <Button
            component={Link}
            to="/login"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.primary' }}
          >
            Login
          </Button>
          <Button
            component={Link}
            to="/signup"
            size="small"
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 999,
              px: 2.25,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: dark ? 'none' : '0 2px 10px rgba(85,107,47,0.30)',
              '&:hover': {
                backgroundColor: dark ? '#adc873' : '#465926',
                boxShadow: dark ? 'none' : '0 4px 14px rgba(85,107,47,0.38)',
              },
            }}
          >
            Sign Up
          </Button>
        </>
      )}
    </>
  );

  const contactRows = (
    <Stack spacing={1.25} sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
      <Stack
        component="a"
        href={phoneHref}
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ color: 'inherit', textDecoration: 'none' }}
      >
        <Phone size={16} />
        <Typography component="span" sx={{ fontSize: 'inherit' }}>
          {SITE.phone}
        </Typography>
      </Stack>
      <Stack
        component="a"
        href={emailHref}
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ color: 'inherit', textDecoration: 'none', wordBreak: 'break-all' }}
      >
        <Mail size={16} />
        <Typography component="span" sx={{ fontSize: 'inherit' }}>
          {SITE.email}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ pt: '2px', display: 'flex' }}>
          <MapPin size={16} />
        </Box>
        <Typography component="span" sx={{ fontSize: 'inherit' }}>
          {SITE.address}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          backgroundColor: dark
            ? `rgba(25, 27, 21, ${scrolled ? 0.94 : 0.84})`
            : `rgba(252, 251, 247, ${scrolled ? 0.94 : 0.86})`,
          backdropFilter: 'blur(14px) saturate(150%)',
          WebkitBackdropFilter: 'blur(14px) saturate(150%)',
          borderBottom: '1px solid',
          borderColor: dark ? 'rgba(244,241,230,0.10)' : 'rgba(85,107,47,0.16)',
          boxShadow: scrolled
            ? dark
              ? '0 6px 24px rgba(0,0,0,0.45)'
              : '0 6px 24px rgba(60,54,40,0.10)'
            : 'none',
          transition: 'background-color .28s ease, box-shadow .28s ease',
        }}
      >
        {/* The first thing a buyer wants is a number to call, so it sits above
            everything — and rolls away once they have started reading. */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            overflow: 'hidden',
            maxHeight: scrolled ? 0 : 40,
            opacity: scrolled ? 0 : 1,
            backgroundColor: dark ? '#2a331d' : '#3f5122',
            color: 'rgba(244,241,230,0.86)',
            transition: 'max-height .3s ease, opacity .2s ease',
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ height: 40, fontSize: '0.78rem' }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <MapPin size={14} />
                <Typography component="span" sx={{ fontSize: 'inherit' }}>
                  {SITE.address}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2.5}>
                <Stack
                  component="a"
                  href={phoneHref}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#fff' } }}
                >
                  <Phone size={14} />
                  <Typography component="span" sx={{ fontSize: 'inherit' }}>
                    {SITE.phone}
                  </Typography>
                </Stack>
                <Stack
                  component="a"
                  href={emailHref}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#fff' } }}
                >
                  <Mail size={14} />
                  <Typography component="span" sx={{ fontSize: 'inherit' }}>
                    {SITE.email}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Container>
        </Box>

        <Container maxWidth="lg" disableGutters>
          <Toolbar
            sx={{
              px: { xs: 2, sm: 3 },
              minHeight: { xs: 64, md: scrolled ? 62 : 78 },
              transition: 'min-height .28s ease',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box
              component={Link}
              to="/home"
              onClick={scrollToTop}
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                transition: 'transform .2s ease',
                '&:hover': { transform: 'translateY(-1px)' },
              }}
            >
              <BrandMark size={scrolled ? 'sm' : 'md'} />
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.25}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {NAV_LINKS.map((link) => (
                <Button
                  key={link.label}
                  component={Link}
                  to={link.to}
                  disableRipple
                  onClick={link.hash === '' ? scrollToTop : undefined}
                  sx={navSx(isLinkActive(link))}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
              {/* One slot, one reveal. It holds its width while the answer is
                  still coming so the rest of the bar does not step sideways. */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                  minWidth: 250,
                }}
              >
                {!authReady ? (
                  <Skeleton variant="rounded" width={220} height={30} />
                ) : (
                  <Fade in timeout={200}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{authControls}</Box>
                  </Fade>
                )}
              </Box>

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', md: 'block' }, my: 1.5 }}
              />

              <Tooltip title={dark ? 'Switch to light' : 'Switch to dark'}>
                <IconButton
                  onClick={colorMode.toggleColorMode}
                  size="small"
                  sx={{ color: 'text.secondary', '&:hover': { color: accent } }}
                >
                  {dark ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                </IconButton>
              </Tooltip>

              <Box sx={{ position: 'relative' }}>
                <Tooltip title="Cart">
                  <IconButton
                    onClick={toggleCart}
                    size="small"
                    aria-label={`Cart, ${cartQuantity} item${cartQuantity === 1 ? '' : 's'}`}
                    sx={{ color: 'text.primary', '&:hover': { color: accent } }}
                  >
                    <Badge
                      badgeContent={cartQuantity}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          minWidth: 17,
                          height: 17,
                        },
                      }}
                    >
                      <ShoppingCartIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
                {isCartOpen && <CartDropdown onClose={toggleCart} />}
              </Box>

              <IconButton
                onClick={() => setIsMenuOpen(true)}
                aria-label="Open menu"
                sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Small screens get the same links, plus the contact details the narrow
          bar has no room to print. */}
      <Drawer
        anchor="right"
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '82vw', sm: 340 },
            maxWidth: 360,
            backgroundImage: 'none',
            display: 'flex',
            flexDirection: 'column',
            px: 2.5,
            py: 2,
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <BrandMark size="sm" />
          <IconButton onClick={() => setIsMenuOpen(false)} aria-label="Close menu" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider />

        <Stack sx={{ py: 1.5 }}>
          {NAV_LINKS.map((link) => {
            const active = isLinkActive(link);
            return (
              <Button
                key={link.label}
                component={Link}
                to={link.to}
                onClick={link.hash === '' ? scrollToTop : undefined}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontSize: '1.02rem',
                  fontWeight: 600,
                  py: 1.15,
                  px: 1.5,
                  borderRadius: 2,
                  color: active ? accent : 'text.primary',
                  backgroundColor: active
                    ? dark
                      ? 'rgba(179,199,127,0.12)'
                      : 'rgba(85,107,47,0.08)'
                    : 'transparent',
                }}
              >
                {link.label}
              </Button>
            );
          })}
        </Stack>

        <Divider />

        <Box sx={{ py: 2 }}>
          {!authReady ? (
            <Skeleton variant="rounded" height={34} />
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {authControls}
            </Stack>
          )}
        </Box>

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          {contactRows}
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
